import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import axiosInstance from '@/libs/axiosInstance';
import type { ApiErrorResponse, ApiResponse } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface DownloadInvoiceResponse {
  url: string;
}

interface UseDownloadPayoutInvoiceActionOptions {
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

export const useDownloadPayoutInvoiceAction = (options?: UseDownloadPayoutInvoiceActionOptions) => {
  const { t } = useTranslation();

  const getErrorMessage = (error: AxiosError<ApiErrorResponse>): string => {
    const errorCode = error.response?.data?.code;

    // Try to translate based on error code (uses errors.{code} pattern)
    if (errorCode) {
      const translationKey = `errors.${errorCode}`;
      const translated = t(translationKey, { defaultValue: '' });
      if (translated && translated !== translationKey) {
        return translated;
      }
    }

    // Fall back to API message or generic error
    return error.response?.data?.message || t('payouts.invoice.error.generic');
  };

  const mutation = useMutation({
    mutationFn: async (batchId: string) => {
      return trackProductAction('documents', 'download', async () => {
        const response = await axiosInstance.get<ApiResponse<DownloadInvoiceResponse>>(
          `/dashboard/payouts/${batchId}/invoice`
        );
        return response.data;
      });
    },
    onSuccess: (response) => {
      const url = (response.data as DownloadInvoiceResponse).url;

      // Open the signed S3 URL in a new tab to trigger download
      window.open(url, '_blank');

      toaster.create({
        title: t('payouts.invoice.success.title'),
        description: t('payouts.invoice.success.description'),
        type: 'success',
        duration: 5000,
      });

      options?.onSuccess?.(url);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      console.error('Error downloading payout invoice:', error);

      const errorMessage = getErrorMessage(error);

      toaster.create({
        title: t('payouts.invoice.error.title'),
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });

      options?.onError?.(error);
    },
  });

  return {
    downloadInvoice: mutation.mutate,
    downloadInvoiceAsync: mutation.mutateAsync,
    isDownloading: mutation.isPending,
  };
};
